import { createReducer, on } from '@ngrx/store';
import { merge, omit } from 'lodash-es';

import { AnyResource } from '@models';
import * as Actions from './actions';
import { initialState } from './initial-state';

const addSelected = (items: AnyResource[]) =>
  items.reduce<Record<number, AnyResource>>((acc, curr) => {
    if (!acc[curr.rid]) {
      acc[curr.rid] = curr;
    }

    return acc;
  }, {});

export const breadcrumbReducer = createReducer(
  initialState.breadcrumbs,
  on(Actions.setBreadcrumbs, (_state, { payload }) => [...(payload ?? [])])
);

export const billingReducer = createReducer(
  initialState.billing,
  on(Actions.setBilling, (state, { payload }) => ({
    ...state,
    billing: { ...payload },
  })),
  on(Actions.updateBilling, (state, { payload }) => ({
    ...state,
    billing: state.billing ? { ...state.billing, ...payload } : state.billing,
  })),
  on(Actions.setSubscription, (state, { payload }) => ({
    ...state,
    subscription: { ...payload },
  })),
  on(Actions.updateSubscription, (state, { payload }) => ({
    ...state,
    subscription: { ...state.subscription, ...payload },
  })),
  on(Actions.setPlanDetails, (state, { payload }) => ({
    ...state,
    planDetails: { ...payload },
  })),
  on(Actions.resetPlanDetails, (state) => ({ ...state, planDetails: null }))
);

export const userReducer = createReducer(
  initialState.user,
  on(Actions.setUserData, (_state, { payload }) => ({ ...payload })),
  on(Actions.updateUserData, (state, { payload }) => merge({}, state, payload)),
  on(Actions.setUserPreferences, (state, { payload }) => ({
    ...state,
    preferences: { ...state.preferences, ...payload },
  })),
  on(Actions.resetUser, () => initialState.user)
);

export const resourceReducer = createReducer(
  initialState.resource,
  on(Actions.setCurrentResource, (state, { payload }) => ({
    ...state,
    current: { ...payload },
  })),
  on(Actions.resetCurrentResource, (state) => ({
    ...state,
    current: null,
  })),
  on(Actions.updateCurrentResource, (state, { payload }) => ({
    ...state,
    current: state.current ? { ...state.current, ...payload } : state.current,
  }))
);

export const conversationReducer = createReducer(
  initialState.conversation,
  on(Actions.setSelectedConversation, (state, { payload }) => ({
    ...state,
    selectedConversation: payload ? { ...payload } : null,
  })),
  on(Actions.setMessages, (state, { payload }) => ({
    ...state,
    messages: [...payload],
  })),
  on(Actions.setConversationList, (state, { payload }) => ({
    ...state,
    list: [...payload],
  })),
  on(Actions.setIsParticipant, (state, { payload }) => ({
    ...state,
    isParticipant: payload,
  })),
  on(Actions.setIsOwner, (state, { payload }) => ({
    ...state,
    isOwner: payload,
  })),
  on(Actions.setUpdateDate, (state, { payload }) => ({
    ...state,
    updateDate: payload,
  })),
  on(Actions.deleteConversation, (state, { payload }) => ({
    ...state,
    list: state.list.filter(({ conversationId: id }) => id !== payload),
  }))
);

export const listReducer = createReducer(
  initialState.list,
  on(Actions.setList, (state, { payload }) => ({
    ...state,
    items: [...payload],
  })),
  on(Actions.emptyList, (state) => ({
    ...state,
    items: [],
    selected: {},
    isAllSelected: false,
  })),
  on(Actions.addSelectedItems, (state, { payload }) => ({
    ...state,
    selected: { ...state.selected, ...addSelected(payload) },
  })),
  on(Actions.removeSelectedItems, (state, { payload }) => ({
    ...state,
    selected: omit(
      state.selected,
      payload.map(({ rid }) => rid)
    ),
    isAllSelected: false,
  })),
  on(Actions.setSelectedItems, (state, { payload }) => ({
    ...state,
    selected: addSelected(payload),
  })),
  on(Actions.emptySelectedItems, (state) => ({
    ...state,
    selected: {},
    isAllSelected: false,
  })),
  on(Actions.setIsAllSelected, (state, { payload }) => ({
    ...state,
    isAllSelected: payload,
  }))
);

export const settingsReducer = createReducer(
  initialState.settings,
  on(Actions.setSystemSettings, (state, { payload }) => ({
    ...state,
    system: payload,
  })),
  on(Actions.setSigningRoomConfig, (state, { payload }) => ({
    ...state,
    signingRoomConfig: payload,
  })),
  on(Actions.setAreZippedFilenamesShortened, (state, { payload }) => ({
    ...state,
    areZippedFilenamesShortened: payload,
  }))
);

export const uiReducer = createReducer(
  initialState.ui,
  on(Actions.setIsModalOpen, (state, { payload }) => ({
    ...state,
    isModalOpen: payload,
  })),
  on(Actions.setIsModalFired, (state, { payload }) => ({
    ...state,
    isModalFired: payload,
  }))
);

export const localeReducer = createReducer(
  initialState.locale,
  on(Actions.setLocale, (_state, { payload }) => payload ?? '')
);

export const languagesReducer = createReducer(
  initialState.languages,
  on(Actions.setLanguages, (_state, { payload }) => [...payload])
);

export const urlReducer = createReducer(
  initialState.url,
  on(Actions.setRequestedUrl, (state, { payload }) => ({
    ...state,
    requestedUrl: payload,
  })),
  on(
    Actions.clearRequestedUrl,
    ({ requestedUrl: _requestedUrl, ...state }) => state
  )
);

export const authReducer = createReducer(
  initialState.auth,
  on(Actions.setIsLoggedIn, (state, { payload }) => ({
    ...state,
    isLoggedIn: payload,
  })),
  on(Actions.setSessionToken, (state, { payload }) => ({
    ...state,
    sessionToken: payload,
  })),
  on(Actions.setIsUserSessionExpiration, (state, { payload }) => ({
    ...state,
    isUserSessionExpiration: payload,
  }))
);

export const navigationReducer = createReducer(
  initialState.navigation,
  on(Actions.setNavigationRoute, (state, { payload }) => ({
    ...state,
    route: payload,
  })),
  on(Actions.setNavigationOpen, (state, { payload }) => ({
    ...state,
    open: payload,
  }))
);

export const viewsReducer = createReducer(
  initialState.views,
  on(Actions.setDefaultView, (state, { payload }) => ({
    ...state,
    defaultView: payload,
  })),
  on(Actions.setUsersView, (state, { payload }) => ({
    ...state,
    usersView: payload,
  })),
  on(Actions.setGroupsView, (state, { payload }) => ({
    ...state,
    groupsView: payload,
  })),
  on(Actions.setRoomTreeView, (state, { payload }) => ({
    ...state,
    roomTreeView: payload,
  })),
  on(Actions.setCalendarView, (state, { payload }) => ({
    ...state,
    calendarView: payload,
  })),
  on(Actions.resetViews, () => ({}))
);

export const rootReducer = createReducer(
  initialState.root,
  on(Actions.setRootData, (state, { payload }) => ({ ...state, ...payload })),
  on(Actions.resetRoot, () => initialState.root)
);

export const communityReducer = createReducer(
  initialState.community,
  on(Actions.setCommunityData, (state, { payload }) => ({
    ...state,
    ...payload,
  })),
  on(Actions.resetCommunity, () => initialState.community)
);

export const sidebarReducer = createReducer(
  initialState.sidebar,
  on(Actions.setIsSidebarExpanded, (state, { payload }) => ({
    ...state,
    isExpanded: payload,
  })),
  on(Actions.toggleIsSidebarExpanded, (state) => ({
    ...state,
    isExpanded: !state.isExpanded,
  })),
  on(Actions.setSidebarVisibility, (state, { payload }) => ({
    ...state,
    isVisible: payload,
  })),
  on(Actions.toggleSidebarVisibility, (state) => ({
    ...state,
    isVisible: !state.isVisible,
  }))
);

export const searchReducer = createReducer(
  initialState.search,
  on(Actions.setLastSearch, (state, { payload }) => ({
    ...state,
    lastSearch: payload,
  })),
  on(Actions.setSearchQuery, (state, { payload }) => ({
    ...state,
    query: payload,
  })),
  on(Actions.setSearchParams, (state, { payload }) => ({
    ...state,
    params: payload,
  })),
  on(Actions.resetSearchParams, (state) => ({
    ...state,
    params: initialState.search.params,
  })),
  on(Actions.updateSearchParams, (state, { payload }) => ({
    ...state,
    params: { ...state.params, ...payload },
  })),
  on(Actions.setOriginPath, (state, { payload }) => ({
    ...state,
    originPath: payload,
  })),
  on(Actions.setIsSearchScopedByResource, (state, { payload }) => ({
    ...state,
    isSearchScopedByCurrentResource: payload,
  })),
  on(Actions.resetIsSearchScopedByResource, (state) => ({
    ...state,
    isSearchScopedByCurrentResource:
      initialState.search.isSearchScopedByCurrentResource,
  }))
);

export const reducer = {
  breadcrumbs: breadcrumbReducer,
  billing: billingReducer,
  user: userReducer,
  resource: resourceReducer,
  conversation: conversationReducer,
  list: listReducer,
  settings: settingsReducer,
  ui: uiReducer,
  locale: localeReducer,
  languages: languagesReducer,
  url: urlReducer,
  auth: authReducer,
  navigation: navigationReducer,
  views: viewsReducer,
  root: rootReducer,
  community: communityReducer,
  sidebar: sidebarReducer,
  search: searchReducer,
};
